<script setup>
/**
 * @description 一级分类 PK 参数对比表
 * @param {Array} products - 固定展示的三列 PK 商品，空位用 null 表示
 * @param {Array} rows - 对比参数行
 * @param {boolean} loading - 是否展示骨架屏
 * @param {boolean} removable - 是否允许移除 PK 商品
 * @emits remove - 移除一列商品
 * @emits add-cart - 加入购物车
 * @emits buy - 立即购买
 */

import { computed, onMounted, ref, watch } from 'vue'
import { IconClose, IconLeft, IconRight } from '@arco-design/web-vue/es/icon'
import BaseButton from '~/components/base/button/Button.vue'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { useUserStore } from '~/store/common/user'
import StoreProductListCard from '~/components/store/productList/Card.vue'
import StoreCategoryPkProductCard from '~/components/store/categoryPk/ProductCard.vue'
import { EMPTY_VALUE_PLACEHOLDER } from '~/utils/constant'

const props = defineProps({
  products: {
    type: Array,
    default: () => [],
  },
  rows: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  removable: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['remove', 'addCart', 'buy'])

const userStore = useUserStore()
const { isMobile } = useMediaQuery()
const canPlaceStoreOrder = computed(() => userStore.canPlaceStoreOrder)

onMounted(() => {
  userStore.ensureStoreAgencyIdentity()
})

/**
 * @description PK 对比表加购前拦截非代理商
 * @param {Object} product
 */
const handleAddCartClick = (product) => {
  if (userStore.guardStoreOrderOrNotify(isMobile.value)) return
  emit('addCart', product)
}

/**
 * @description PK 对比表立即购买前拦截非代理商
 * @param {Object} product
 */
const handleBuyClick = (product) => {
  if (userStore.guardStoreOrderOrNotify(isMobile.value)) return
  emit('buy', product)
}

const fixedProducts = computed(() => {
  const source = Array.isArray(props.products) ? props.products : []
  if (!props.removable) {
    return source.filter(Boolean).slice(0, 3)
  }
  return [source[0] || null, source[1] || null, source[2] || null]
})

const mobileProductIndex = ref(0)

const mobileProductEntries = computed(() => {
  return fixedProducts.value
    .map((product, index) => ({
      product,
      valueIndex: index,
    }))
    .filter((entry) => Boolean(entry.product))
})

const mobileProductEntry = computed(() => {
  return mobileProductEntries.value[mobileProductIndex.value] || null
})

const mobileProduct = computed(() => mobileProductEntry.value?.product || null)

const mobileValueIndex = computed(() => mobileProductEntry.value?.valueIndex || 0)

const canSwitchMobileLeft = computed(() => mobileProductIndex.value > 0)

const canSwitchMobileRight = computed(() => {
  return mobileProductIndex.value < mobileProductEntries.value.length - 1
})

const shouldShowMobileNav = computed(() => mobileProductEntries.value.length > 1)

const productColumnCount = computed(() => Math.max(fixedProducts.value.length, 1))

const tableGridStyle = computed(() => ({
  '--category-pk-product-columns': productColumnCount.value,
}))

const actionGridStyle = computed(() => ({
  '--category-pk-product-columns': productColumnCount.value,
}))

/**
 * @description 获取商品 ID
 * @param {Object} product - 商品数据
 * @returns {string|number}
 */
const getProductId = (product) => product?.prodId || product?.id

/**
 * @description 切换移动端当前展示的 PK 商品
 * @param {number} direction - 切换方向，-1 向左，1 向右
 * @returns {void}
 */
const switchMobileProduct = (direction) => {
  const nextIndex = mobileProductIndex.value + direction
  const maxIndex = mobileProductEntries.value.length - 1
  mobileProductIndex.value = Math.max(0, Math.min(nextIndex, maxIndex))
}

watch(
  () => mobileProductEntries.value.map((entry) => getProductId(entry.product)).join(','),
  () => {
    const maxIndex = mobileProductEntries.value.length - 1
    mobileProductIndex.value = Math.max(0, Math.min(mobileProductIndex.value, maxIndex))
  }
)
</script>

<template>
  <section class="category-pk-table" aria-label="参数对比">
    <template v-if="loading">
      <div class="category-pk-table__skeleton">
        <a-skeleton animation>
          <a-skeleton-line :rows="8" :widths="['100%', '100%', '100%', '100%']" />
        </a-skeleton>
      </div>
    </template>

    <template v-else>
      <div class="category-pk-table__scroll category-pk-table__scroll--desktop">
        <table class="category-pk-table__table" :style="tableGridStyle">
          <colgroup>
            <col class="category-pk-table__col category-pk-table__col--label" />
            <col
              v-for="columnIndex in productColumnCount"
              :key="`category-pk-col-${columnIndex}`"
              class="category-pk-table__col category-pk-table__col--product"
            />
          </colgroup>
          <thead>
            <tr>
              <th
                class="category-pk-table__label category-pk-table__label--head"
                aria-hidden="true"
              ></th>
              <th
                v-for="(product, index) in fixedProducts"
                :key="product ? getProductId(product) : `empty-${index}`"
                class="category-pk-table__product-cell"
              >
                <StoreProductListCard
                  v-if="product"
                  :product="product"
                  :show-cart="false"
                  :subtitle-line="1"
                  class="category-pk-table__product-card"
                >
                  <template v-if="removable && index !== 0" #actions>
                    <button
                      type="button"
                      class="category-pk-table__remove"
                      aria-label="移除比较商品"
                      @click.stop.prevent="emit('remove', getProductId(product))"
                    >
                      <IconClose />
                    </button>
                  </template>
                </StoreProductListCard>
                <div v-else class="category-pk-table__empty-slot">
                  <SvgIcon name="furniture-equipment" size="44" />
                  <span>从上面选择</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key || row.label" class="category-pk-table__row">
              <th
                class="category-pk-table__label"
                :class="{
                  'category-pk-table__label--category-row': row.isCategory,
                  'category-pk-table__label--nested': row.level > 0,
                  'category-pk-table__label--leaf': row.level > 0 && !row.isCategory,
                }"
                :style="{ '--category-pk-tree-level': row.level || 0 }"
              >
                <div class="category-pk-table__label-tree">
                  <span class="category-pk-table__label-text">{{ row.label }}</span>
                </div>
              </th>
              <td
                v-if="row.isCategory"
                :colspan="fixedProducts.length"
                class="category-pk-table__value category-pk-table__value--category"
                aria-hidden="true"
              ></td>
              <td
                v-for="(product, index) in fixedProducts"
                v-else
                :key="
                  product
                    ? `${row.key || row.label}-${getProductId(product)}`
                    : `${row.key || row.label}-${index}`
                "
                class="category-pk-table__value"
              >
                {{ product ? row.values[index] || EMPTY_VALUE_PLACEHOLDER : '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="canPlaceStoreOrder"
        class="category-pk-table__actions"
        :style="actionGridStyle"
        aria-label="对比商品操作"
      >
        <div class="category-pk-table__action-spacer"></div>
        <div
          v-for="(product, index) in fixedProducts"
          :key="product ? `action-${getProductId(product)}` : `action-empty-${index}`"
          class="category-pk-table__action-cell"
        >
          <template v-if="product">
            <div class="category-pk-table__action-buttons">
              <BaseButton
                aria-label="加入购物车"
                class="category-pk-table__outline-btn"
                type="default"
                @click="handleAddCartClick(product)"
              >
                加入购物车
              </BaseButton>
              <BaseButton
                aria-label="立即购买"
                class="category-pk-table__primary-btn"
                @click="handleBuyClick(product)"
              >
                立即购买
              </BaseButton>
            </div>
          </template>
        </div>
      </div>

      <div class="category-pk-table__mobile">
        <div class="category-pk-table__mobile-parameter">
          <StoreCategoryPkProductCard
            v-if="mobileProduct"
            :key="getProductId(mobileProduct)"
            :product="mobileProduct"
            is-anchor
            class="category-pk-table__mobile-card"
          />
          <div
            v-if="shouldShowMobileNav"
            class="category-pk-table__mobile-nav-boundary"
            aria-label="移动端对比商品切换"
          >
            <div class="category-pk-table__mobile-nav">
              <button
                v-if="canSwitchMobileLeft"
                aria-label="查看上一个对比商品"
                type="button"
                class="category-pk-table__mobile-nav-btn category-pk-table__mobile-nav-btn--prev"
                @click="switchMobileProduct(-1)"
              >
                <IconLeft />
              </button>
              <button
                v-if="canSwitchMobileRight"
                aria-label="查看下一个对比商品"
                type="button"
                class="category-pk-table__mobile-nav-btn category-pk-table__mobile-nav-btn--next"
                @click="switchMobileProduct(1)"
              >
                <IconRight />
              </button>
            </div>
          </div>
          <div class="category-pk-table__mobile-rows">
            <div
              v-for="row in rows"
              :key="row.key || row.label"
              class="category-pk-table__mobile-row"
              :class="{ 'category-pk-table__mobile-row--category': row.isCategory }"
            >
              <span class="category-pk-table__mobile-label">{{ row.label }}</span>
              <span class="category-pk-table__mobile-value">
                {{
                  row.isCategory ? '' : row.values?.[mobileValueIndex] || EMPTY_VALUE_PLACEHOLDER
                }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="canPlaceStoreOrder && mobileProduct" class="category-pk-table__mobile-actions">
          <BaseButton
            aria-label="加入购物车"
            class="category-pk-table__outline-btn"
            type="default"
            @click="handleAddCartClick(mobileProduct)"
          >
            加入购物车
          </BaseButton>
          <BaseButton
            aria-label="立即购买"
            class="category-pk-table__primary-btn"
            @click="handleBuyClick(mobileProduct)"
          >
            立即购买
          </BaseButton>
        </div>
      </div>
    </template>
  </section>
</template>

<style lang="scss" scoped>
.category-pk-table {
  --category-pk-label-column-width: 338px;

  width: 100%;
  border-radius: $radius-6;

  &__skeleton {
    padding: 32px;
    border: 1px solid $border-dashed-color;
    border-radius: $radius-6;
    background-color: $bg-white;
  }

  &__scroll {
    width: 100%;
    overflow: visible;
    border: 1px solid $border-dashed-color;
    border-radius: $radius-6;
    background-color: $bg-white;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__mobile {
    display: none;
  }

  &__table {
    width: 100%;
    min-width: 980px;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
  }

  &__col {
    &--label {
      width: var(--category-pk-label-column-width);
    }

    &--product {
      width: calc(
        (100% - var(--category-pk-label-column-width)) / var(--category-pk-product-columns)
      );
    }
  }

  th,
  td {
    border-right: 1px solid $border-dashed-color;
    border-bottom: 1px solid $border-dashed-color;

    &:last-child {
      border-right: 0;
    }
  }

  tbody tr:last-child {
    th,
    td {
      border-bottom: 0;
    }
  }

  thead {
    position: sticky;
    top: calc(#{$base-header-height} - 216px);
    z-index: 20;
    background-color: $bg-white;
    box-shadow: 0 10px 16px -14px rgba($color-black, 0.45);

    th {
      border-right: 0;
      border-bottom: 0;
      background-color: $bg-white;
    }
  }

  &__label {
    position: relative;
    width: var(--category-pk-label-column-width);
    height: 64px;
    padding: 9.5px 16px;
    color: $text-title-color;
    font-size: $font-size-card-content;
    font-weight: $font-weight-medium;
    line-height: $line-height-card-content;
    text-align: left;
    vertical-align: middle;
    background-color: $bg-white;

    &--head {
      height: 292px;
      padding: 0 16px;
      text-align: center;
    }

    &--category-row {
      color: $text-title-color;
      font-weight: $font-weight-medium;
    }

    &--nested {
      --category-pk-tree-indent-step: 16px;

      .category-pk-table__label-tree {
        padding-left: calc(var(--category-pk-tree-level) * var(--category-pk-tree-indent-step));
      }
    }

    &--leaf {
      color: $text-content-color;
      font-weight: 600;
    }
  }

  &__label-tree {
    min-width: 0;
    padding-left: 0;
  }

  &__label-text {
    display: block;
    min-width: 0;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  &__product-cell {
    height: 324px;
    padding: 0;
    vertical-align: stretch;
    background-color: $bg-white;
  }

  &__product-card {
    position: relative;
    z-index: 1;
    height: 100%;
    box-shadow: none;

    &:hover,
    &:focus-visible {
      box-shadow: none;
    }

    &:hover {
      :deep(.product-list-card__inner),
      :deep(.product-list-card__image),
      :deep(.product-list-card__actions) {
        transform: none !important;
      }
    }

    :deep(.product-list-card__actions) {
      top: 0;
      right: 0;
      opacity: 1;
      transform: scale(1);
    }

    :deep(.product-list-card__content),
    :deep(.product-list-card__info),
    :deep(.product-list-card__price-row),
    :deep(.product-list-card__price) {
      align-items: center;
      text-align: center;
    }

    :deep(.product-list-card__title),
    :deep(.product-list-card__subtitle) {
      width: 100%;
      min-width: 0;
      max-width: 100%;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: center;
    }

    :deep(.product-list-card__subtitle) {
      font-weight: 400;
    }

    :deep(.product-list-card__info),
    :deep(.product-list-card__title-row) {
      width: 100%;
      min-width: 0;
      max-width: 100%;
    }

    :deep(.product-list-card__title-row) {
      margin-bottom: 8px;
    }

    :deep(.product-list-card__model) {
      display: none;
    }

    :deep(.product-list-card__price-row) {
      justify-content: center;
      padding-right: 0;
    }

    :deep(.product-list-card__subtitle--single-line) {
      min-height: 22px;
    }

    :deep(.product-list-card__inner),
    :deep(.product-list-card__image) {
      transform: none;
    }
  }

  &__empty-slot {
    min-height: 244px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: $font-size-card-content;
    line-height: $line-height-card-content;
  }

  &__remove {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background-color: $border-hover-color;
    color: $bg-white;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: $text-title-color;
    }
  }

  &__value {
    height: 64px;
    padding: 9.5px 16px;
    color: $text-content-color;
    font-size: $font-size-card-content;
    line-height: $line-height-card-content;
    text-align: center;
    vertical-align: middle;
    white-space: pre-line;
    word-break: break-word;
    overflow-wrap: anywhere;
    background-color: $bg-white;

    &--category {
      color: $text-subtitle-color;
    }
  }

  &__actions {
    display: grid;
    grid-template-columns:
      var(--category-pk-label-column-width)
      repeat(var(--category-pk-product-columns), minmax(0, 1fr));
    gap: 0;
    min-width: 980px;
    padding: 32px 0 0;
  }

  &__action-cell {
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  &__action-buttons {
    display: flex;
    justify-content: center;
    gap: 8px;
  }
}

@include respond-to-max('lg') {
  .category-pk-table {
    --category-pk-label-column-width: 140px;

    &__scroll {
      &--desktop {
        display: none;
      }
    }

    &__actions {
      display: none;
    }

    &__mobile {
      display: block;
      position: relative;
    }

    &__mobile-parameter {
      --category-pk-mobile-nav-bottom-gap: 220px;
      --category-pk-mobile-nav-start-offset: 174px;

      position: relative;
    }

    &__mobile-title {
      display: none;
    }

    &__mobile-card {
      --category-pk-mobile-card-image-offset: 216px;

      position: sticky;
      top: calc(#{$base-header-height-mobile} - var(--category-pk-mobile-card-image-offset));
      z-index: 18;
      width: 100%;
      margin-bottom: 24px;
      background-color: $bg-white;
      box-shadow: 0 8px 16px -12px rgba($color-black, 0.35);

      :deep(.category-pk-product-card),
      :deep(.product-list-card__inner) {
        width: 100%;
      }

      :deep(.product-list-card__info),
      :deep(.product-list-card__price-row),
      :deep(.product-list-card__price) {
        align-items: center;
        text-align: center;
      }

      :deep(.product-list-card__content) {
        width: 100%;
        min-width: 0;
        align-items: stretch;
        text-align: center;
      }

      :deep(.product-list-card__info) {
        width: 100%;
        min-width: 0;
        max-width: 100%;
        min-height: 56px;
      }

      :deep(.product-list-card__title-row) {
        width: 100%;
        min-width: 0;
        max-width: 100%;
      }

      :deep(.product-list-card__title),
      :deep(.product-list-card__subtitle) {
        width: 100%;
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: center;
      }

      :deep(.product-list-card__model) {
        display: none;
      }

      :deep(.product-list-card__price-row) {
        justify-content: center;
        padding-right: 0;
      }
    }

    &__mobile-nav-boundary {
      position: absolute;
      top: var(--category-pk-mobile-nav-start-offset);
      right: 0;
      bottom: var(--category-pk-mobile-nav-bottom-gap);
      left: 0;
      z-index: 20;
      pointer-events: none;
    }

    &__mobile-nav {
      position: sticky;
      top: 50vh;
      width: 100%;
      height: 0;
      pointer-events: none;
    }

    &__mobile-nav-btn {
      position: absolute;
      top: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      padding: 0;
      border: 1px solid rgba($border-dashed-color, 0.45);
      border-radius: 50%;
      background-color: rgba($text-title-color, 0.3);
      color: $bg-white;
      backdrop-filter: blur(8px);
      box-shadow: 0 4px 12px rgba($color-black, 0.1);
      cursor: pointer;
      pointer-events: auto;
      transform: translateY(-50%);
      transition:
        background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover,
      &:focus-visible {
        background-color: $primary-color;
        border-color: $primary-color;
        color: $bg-white;
        box-shadow: 0 6px 16px rgba($primary-color, 0.3);
        outline: none;
        transform: translateY(-50%) scale(1.1);
      }

      &:active {
        transform: translateY(-50%) scale(0.95);
      }

      :deep(.arco-icon) {
        font-size: 20px;
      }
    }

    &__mobile-nav-btn--prev {
      left: 16px;
    }

    &__mobile-nav-btn--next {
      right: 16px;
    }

    &__mobile-rows {
      border-top: 1px solid $border-light-color;
    }

    &__mobile-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
      min-height: 56px;
      border-bottom: 1px solid $border-light-color;
      color: $text-title-color;
      font-size: 14px;
      line-height: 22px;
    }

    &__mobile-row--category {
      display: none;
    }

    &__mobile-label,
    &__mobile-value {
      display: flex;
      align-items: center;
      min-width: 0;
      padding: 5.5px 0;
      word-break: break-word;
    }

    &__mobile-label {
      font-weight: $font-weight-medium;
    }

    &__mobile-value {
      justify-content: flex-end;
      color: $text-content-color;
      text-align: right;
    }

    &__mobile-actions {
      display: flex;
      justify-content: center;
      gap: 8px;
      padding-top: $spacing-xl;
    }

    &__outline-btn,
    &__primary-btn {
      width: 112px;
      height: 44px;
      padding: 0 12px;
    }
  }
}
</style>
